import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { assetsAPI } from '@/features/assets/api/api'
import type { Asset } from '@/features/assets/api/types'
import type { RootState } from './store'

interface AssetsState {
  items: Asset[]
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  lastFetchedAt: number | null
}

const initialState: AssetsState = {
  items: [],
  status: 'idle',
  lastFetchedAt: null,
}

export const fetchAssets = createAsyncThunk(
  'assets/fetch',
  async (_, { getState }) => {
    const { portfolio } = getState() as RootState
    const params: Record<string, unknown> = {}
    if (portfolio.activeId) params.portfolio_id = portfolio.activeId
    const res = await assetsAPI.assets.list(params)
    if (res.success) return res.data
    throw new Error(res.message)
  },
  {
    condition: (_, { getState }) => {
      const { assets } = getState() as RootState
      return assets.status !== 'loading'
    },
  }
)

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    invalidateAssets: (state) => {
      state.lastFetchedAt = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssets.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.items = action.payload
        state.status = 'succeeded'
        state.lastFetchedAt = Date.now()
      })
      .addCase(fetchAssets.rejected, (state) => {
        state.status = 'failed'
      })
  },
})

export const { invalidateAssets } = assetsSlice.actions
export default assetsSlice.reducer
